import './CustomForm.less';
import React from 'react';
import {translate} from '../../i18n';
import getInputComponent, {normalizeValue} from './input-component/index';
import checkError, {checkIsRequired, getRuleValue} from './error-checking';
import Icon from "../../components/icon/Icon";
import Toasts, {TOAST_WARN} from '../../scomponents/toasts/Toasts';
import Button from "../../components/button/Button";
import {jsonCompare, jsonCopy} from "../../utils/json";


export default class CustomForm extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            values: {},
            errors: {},
            fieldsTouched: {},
            isSubmitting: false,
            submitError: null,
            resets: false,
        };

        this.initValues();

        this.defaultValuesJson = JSON.stringify(this.state.values);

        this.setSubmitInvoker();
        this.setCheckHasChangesInvoker();
        this.setForceFieldsTouchedInvoker();
    }

    static getDerivedStateFromProps(props, state) {
        const {fieldConfigs, fieldsConfig, defaultValues = {}} = props;
        let hasChange = false;

        if (fieldConfigs !== undefined) {
            // delete no longer existed keys
            for (let key in state.values) {
                if (state.values.hasOwnProperty(key)) {
                    if (!fieldConfigs.find(t => t.key === key)) {
                        delete state.values[key];
                        hasChange = true;
                    }
                }
            }

            // add new keys
            fieldConfigs.forEach(({key, type, defaultValue}) => {
                if (!state.values.hasOwnProperty(key)) {
                    if (defaultValue === undefined) {
                        defaultValue = defaultValues[key];
                    }
                    state.values[key] = normalizeValue(defaultValue, type);
                    hasChange = true;
                }
            });

            // force values
            fieldConfigs.forEach(({key, type, value}) => {
                if (value !== undefined) {
                    state.values[key] = normalizeValue(value, type);
                    hasChange = true;
                }
            });

        } else {
            // delete no longer existed keys
            for (let key in state.values) {
                if (state.values.hasOwnProperty(key)) {
                    if (!fieldsConfig.hasOwnProperty(key)) {
                        delete state.values[key];
                        hasChange = true;
                    }
                }
            }

            // add new keys
            for (let key in fieldsConfig) {
                if (fieldsConfig.hasOwnProperty(key) && !state.values.hasOwnProperty(key)) {
                    let {type, defaultValue} = fieldsConfig[key];
                    if (defaultValue === undefined) {
                        defaultValue = defaultValues[key];
                    }
                    state.values[key] = normalizeValue(defaultValue, type);
                    hasChange = true;
                }
            }

            // force values
            for (let key in fieldsConfig) {
                if (fieldsConfig.hasOwnProperty(key)) {
                    const {type, value} = fieldsConfig[key];
                    if (value !== undefined) {
                        state.values[key] = normalizeValue(value, type);
                        hasChange = true;
                    }
                }
            }
        }

        if (hasChange) {
            return state;
        }

        return null;
    }

    initValues() {
        const {fieldConfigs, fieldsConfig, defaultValues} = this.props;

        if (fieldConfigs !== undefined) {
            fieldConfigs.forEach(({key, type, defaultValue}) => {
                if (defaultValue === undefined) {
                    defaultValue = defaultValues[key];
                }
                this.state.values[key] = normalizeValue(defaultValue, type);
            });
            return;
        }

        for (let key in fieldsConfig) {
            if (fieldsConfig.hasOwnProperty(key)) {
                let {type, defaultValue} = fieldsConfig[key];
                if (defaultValue === undefined) {
                    defaultValue = defaultValues[key];
                }
                this.state.values[key] = normalizeValue(defaultValue, type);
            }
        }
    }

    componentDidMount() {
        this.handleOnInitialized();
    }

    handleOnInitialized = () => {
        const {onInitialized} = this.props;
        const {values, errors} = this.state;
        this.validate(onInitialized ? () => onInitialized(values, errors) : undefined);
    };

    /**
     *
     * @type {function[]}
     */
    onSubmitSuccessHandlers = [];

    /**
     *
     * @type {function[]}
     */
    onSubmitErrorHandlers = [];

    onSubmitSuccess = () => {
        this.onSubmitSuccessHandlers.forEach(handler => handler());
    };

    onSubmitError = (errMsg) => {
        this.onSubmitErrorHandlers.forEach(handler => handler(errMsg));
    };

    setSubmitInvoker = () => {
        const {onSetSubmitInvoker} = this.props;

        if (!onSetSubmitInvoker) {
            return;
        }

        const submit = () => this.submit();
        const addSubmitSuccessHandler = handler => this.onSubmitSuccessHandlers.push(handler);
        const addSubmitErrorHandler = handler => this.onSubmitErrorHandlers.push(handler);

        onSetSubmitInvoker(submit, addSubmitSuccessHandler, addSubmitErrorHandler);
    };

    setCheckHasChangesInvoker = () => {
        const {onSetCheckHasChangesInvoker} = this.props;

        if (!onSetCheckHasChangesInvoker) {
            return;
        }

        onSetCheckHasChangesInvoker(() => this.hasChangesToSubmit());
    };

    setForceFieldsTouchedInvoker = () => {
        const {onSetForceFieldsTouchedInvoker} = this.props;

        if (!onSetForceFieldsTouchedInvoker) {
            return;
        }

        onSetForceFieldsTouchedInvoker(touched => this.forceFieldsTouched(touched));
    };


    hasChangesToSubmit = () => {
        const {values} = this.state;
        const defaultValues = JSON.parse(this.defaultValuesJson);

        return this.getSubmittableAffectedFields().some(key => {
            return !this.checkValuesAreEqual(key, values[key], defaultValues[key]);
        });
    };

    checkValuesAreEqual = (key, val1, val2) => {
        const {type, options = {}} = this.getFieldConfig(key);

        const comparator = (a, b) => {
            if (a > b) return 1;
            if (a < b) return -1;
            return 0;
        };

        if (type === 'leftRightSelect') {
            let idAr1 = val1.map(item => item.id);
            let idAr2 = val2.map(item => item.id);

            if (!options.sortable) {
                idAr1.sort(comparator);
                idAr2.sort(comparator);
            }

            return jsonCompare(idAr1, idAr2);
        }

        if (['multiSelect', 'leftRightGroupableSelect', 'pillbox'].includes(type)) {
            let cln1 = [].concat(val1);
            let cln2 = [].concat(val2);

            if (!options.sortable) {
                cln1.sort(comparator);
                cln2.sort(comparator);
            }

            return jsonCompare(cln1, cln2);
        }

        return jsonCompare(val1, val2);
    };

    setValue = (key, value) => {
        const {onChange} = this.props;
        const {values, errors, fieldsTouched} = this.state;

        if (this.checkValuesAreEqual(key, value, values[key])) {
            return;
        }

        fieldsTouched[key] = true;
        values[key] = value;

        // update error instantly to prevent jumping when waiting for validate in callback
        let error = this.getError(key, value);
        if (error !== null) {
            errors[key] = error;
        } else {
            delete errors[key];
        }

        this.setState({values, errors, fieldsTouched}, () => {
            const {values} = this.state;

            // call onChange
            // to get updates of configs depended on values
            onChange && onChange(
                jsonCopy(values),
                jsonCopy(errors),
                false, // before validation
                key // triggered field
            );

            // setTimeout with delay = 0
            // to add to callback queue
            // to make validate method runs after onChange (which is async)
            setTimeout(() => {
                this.validate(() => {
                    const {values, errors} = this.state;
                    onChange && onChange(
                        jsonCopy(values),
                        jsonCopy(errors),
                        true, // after validation
                        key // triggered field
                    );
                });
            }, 0);
        });
    };

    getSubmittableAffectedFields = () => {
        const {submit, submitWYS, submitChanges} = this.props;
        const {values} = this.state;

        const {fieldConfigs, fieldsConfig} = this.props;

        const filterField = field => {
            const config = fieldConfigs !== undefined ? fieldConfigs.find(config => config.key === field) : fieldsConfig[field];
            return !config.isAttachedInfo;
        };

        if (submitWYS && !submit && !submitChanges) {
            return this.getDisplayedFields().filter(filterField);
        }

        return Object.keys(values).filter(filterField);
    };

    getDisplayedFields = () => {
        const result = [];
        const {fieldConfigs, fieldsConfig, layoutConfig} = this.props;

        if (fieldConfigs) {
            fieldConfigs.forEach(({key, hidden}) => {
                if (hidden) {
                    return;
                }
                result.push(key);
            });
            return result;
        }

        const getFields = (item) => {
            const fields = [];
            if (React.isValidElement(item)) {
                return fields;
            }
            if (typeof item === 'string') {
                if (!fieldsConfig[item].hidden) {
                    fields.push(item);
                }
                return fields;
            }
            item.children.forEach(child => {
                fields.push(...getFields(child));
            });
            return fields;
        };
        layoutConfig.forEach(item => result.push(...getFields(item)));
        return result;
    };

    getIsFieldDisplayed = (field) => {
        const {fieldConfigs, fieldsConfig, layoutConfig} = this.props;
        const {hidden = false} = fieldConfigs !== undefined ? fieldConfigs.find(config => config.key === field) : fieldsConfig[field];

        // ignore if hidden
        if (hidden) {
            return false;
        }

        // ignore if not in layout
        if (fieldsConfig) {
            const checkItem = (item) => {
                if (React.isValidElement(item)) {
                    return false;
                }
                if (typeof item === 'string') {
                    return item === field;
                }
                return item.children.some(child => {
                    return checkItem(child);
                });
            };
            return layoutConfig.some(item => checkItem(item));
        }

        return true;
    };

    getFieldConfig = (key) => {
        const {fieldConfigs, fieldsConfig} = this.props;

        if (fieldConfigs !== undefined) {
            return fieldConfigs.find(config => config.key === key);
        }

        return fieldsConfig[key];
    };

    getError(key, value) {
        let error = null;

        const fieldConfig = this.getFieldConfig(key);
        const {type, rules = {}, options = {}} = fieldConfig;
        const {values} = this.state;

        // ignore if hidden
        if (!this.getIsFieldDisplayed(key)) {
            return error;
        }

        return checkError(type, value, rules, options, values);
    }

    validate = (callback) => {
        const {values, errors} = this.state;

        let key;

        // check error for each keys
        for (key in values) {
            if (values.hasOwnProperty(key)) {
                const error = this.getError(key, values[key]);
                if (error !== null) {
                    errors[key] = error;
                } else {
                    delete errors[key];
                }
            }
        }

        // delete no longer existed keys
        for (key in errors) {
            if (errors.hasOwnProperty(key) && !values.hasOwnProperty(key)) {
                delete errors[key];
            }
        }

        this.setState({errors}, () => {
            callback && callback();
        });
    };

    showErrorToast = () => {
        const {errors} = this.state;

        for (let key in errors) {
            if (errors.hasOwnProperty(key)) {
                const {fieldConfigs, fieldsConfig} = this.props;
                const {name} = fieldConfigs !== undefined ? fieldConfigs.find(config => config.key === key) : fieldsConfig[key];
                Toasts.add(TOAST_WARN, translate('::field: ::error', {field: name, error: errors[key]}));
                break;
            }
        }
    };

    submit = () => {
        const {submit, submitWYS, submitChanges, onSubmittingChange} = this.props;
        const {isSubmitting} = this.state;

        if (isSubmitting) {
            return false;
        }

        this.validate(() => {
            const {values, errors} = this.state;

            if (Object.keys(errors).length === 0) {
                // blur active field inside form
                if (document.activeElement && this.form.contains(document.activeElement)) {
                    document.activeElement.blur();
                }

                // start submitting
                this.setState({
                    isSubmitting: true,
                    submitError: null
                }, () => {
                    const onSuccess = () => {
                        this.defaultValuesJson = JSON.stringify(values);
                        this.setState({
                            isSubmitting: false,
                            submitError: null
                        }, () => {
                            onSubmittingChange && onSubmittingChange(false);
                            this.onSubmitSuccess();
                        });
                    };

                    const onError = (errMsg) => {
                        let {submitError, errors} = this.state;

                        if (typeof errMsg === 'object') {
                            Object.assign(errors, errMsg);
                        } else if (typeof errMsg === 'string') {
                            submitError = errMsg
                        }

                        this.setState({
                            isSubmitting: false,
                            submitError,
                            errors
                        }, () => {
                            onSubmittingChange && onSubmittingChange(false);
                            this.onSubmitError(errMsg);
                        });
                    };

                    onSubmittingChange && onSubmittingChange(true);

                    if (submit) {
                        submit(jsonCopy(values), onSuccess, onError);
                    }

                    if (submitWYS) {
                        const displayedValues = {};
                        this.getDisplayedFields().forEach(field => {
                            displayedValues[field] = values[field];
                        });
                        submitWYS(displayedValues, onSuccess, onError);
                    }

                    if (submitChanges) {
                        const defaultValues = JSON.parse(this.defaultValuesJson);

                        const changedValues = {};
                        for (let field in values) {
                            if (values.hasOwnProperty(field)) {
                                if (!this.checkValuesAreEqual(field, values[field], defaultValues[field])) {
                                    changedValues[field] = values[field];
                                }
                            }
                        }

                        submitChanges(changedValues, onSuccess, onError);
                    }
                });

                return;
            }

            // set all fields are touched to make errors can be displayed
            this.forceFieldsTouched();

            //this.showErrorToast();
        });
    };

    forceFieldsTouched = (touched = true) => {
        const {fieldConfigs, fieldsConfig} = this.props;
        const {fieldsTouched} = this.state;

        if (fieldConfigs !== undefined) {
            fieldConfigs.forEach(({key}) => {
                fieldsTouched[key] = touched;
            });
        } else {
            for (let key in fieldsConfig) {
                if (fieldsConfig.hasOwnProperty(key)) {
                    fieldsTouched[key] = touched;
                }
            }
        }

        this.setState({fieldsTouched});
    };

    cancel = () => {
        this.setState({
            values: JSON.parse(this.defaultValuesJson),
            fieldsTouched: {},
            submitError: null,
            resets: true
        }, () => {
            const {cancel} = this.props;

            if (cancel) {
                cancel(this.hasChangesToSubmit());
            }

            this.setState({resets: false});
        });
    };

    renderField = ({
                       name,
                       hint,
                       valueHint,
                       key,
                       type,
                       defaultValue,
                       value: forcedValue,
                       disabled = false,
                       rules = {},
                       options = {},
                       data = {},
                   }) => {

        const {values, errors, fieldsTouched, resets} = this.state;
        const value = values[key];
        const error = errors[key];
        const touched = fieldsTouched[key];
        const ruleRequired = getRuleValue(rules.required, values);
        const isRequired = checkIsRequired(type, rules, values);
        const fieldNameShown = !!name && !['checkbox', 'arrowToggle'].includes(type);
        const fieldHintShown = !!hint;

        if (resets) {
            forcedValue = value;
        }

        return <div
            key={key}
            className={
                'field'
                + (isRequired ? ' required' : '')
                + (error !== undefined ? ' has-error' : '')
            }
            {...{'data-field': key}}
        >
            <div className={'field-heading' + (!fieldNameShown && !fieldHintShown ? ' empty' : '')}>
                {
                    (fieldNameShown || fieldHintShown) &&
                    <div className="field-heading-inner">
                        {
                            fieldNameShown &&
                            <div className="field-name">
                                <span className="name-text">{name}</span>
                                {
                                    !isRequired &&
                                    !disabled &&
                                    !options.optionalTextHidden &&
                                    <span className="optional-text">{translate('(Optional)')}</span>
                                }
                            </div>
                        }
                        {
                            fieldHintShown &&
                            <div className="field-hint">{hint}</div>
                        }
                    </div>
                }
            </div>
            <div className="field-body">
                <div className="field-body-inner">
                    <div className="field-input">
                        {
                            getInputComponent(type, {
                                values,
                                key,
                                name,
                                value,
                                defaultValue,
                                forcedValue,
                                data,
                                rules,
                                options,
                                disabled,
                                ruleRequired,
                                isRequired,
                                onChange: newValue => this.setValue(key, newValue),
                            })
                        }
                    </div>
                    {
                        error !== undefined &&
                        touched &&
                        <div className="field-error">{error}</div>
                    }
                    {
                        valueHint &&
                        <div className="field-value-hint">{valueHint}</div>
                    }
                </div>
            </div>
        </div>;
    };

    renderGroup = ({
                       name,
                       hint,
                       valueHint,
                       children,
                       direction = 'column',
                       space = 'medium',
                       itemWeights = undefined,
                       alignItems = undefined,
                   }) => {

        const {fieldsConfig} = this.props;
        const {values} = this.state;
        const groupKey = JSON.stringify(children.filter(child => typeof child === 'string'));
        const isRequired = children.filter(child => typeof child === 'string').some(child => {
            const {type, rules = {}} = fieldsConfig[child];
            return checkIsRequired(type, rules, values);
        });
        const fieldNameShown = !!name;
        const fieldHintShown = !!hint;

        return <div
            className="group"
            key={groupKey}
        >
            <div className={'group-heading' + (!fieldNameShown && !fieldHintShown ? ' empty' : '')}>
                {
                    (fieldNameShown || fieldHintShown) &&
                    <div className="group-heading-inner">
                        {
                            fieldNameShown &&
                            <div className="group-name">
                                <span className="name-text">{name}</span>
                                {
                                    !isRequired &&
                                    <span className="optional-text">{translate('(Optional)')}</span>
                                }
                            </div>
                        }
                        {
                            fieldHintShown &&
                            <div className="group-hint">{hint}</div>
                        }
                    </div>
                }
            </div>
            <div className="group-body">
                <div className="group-body-inner">
                    <div className={`group-content direction-${direction} space-${space}`}
                         style={direction === 'row' ? {
                             alignItems: alignItems !== undefined
                                 ? (alignItems === 'center' ? alignItems : `flex-${alignItems}`)
                                 : undefined
                         } : undefined}
                    >
                        {
                            children.map((child, index) => <div
                                key={index}
                                className="group-child"
                                style={direction === 'row' ? {
                                    flex: itemWeights !== undefined ? `${itemWeights[index]} 0 0` : undefined
                                } : undefined}
                            >
                                {
                                    typeof child === 'string'
                                        ? this.renderField(Object.assign(fieldsConfig[child], {key: child}))
                                        : (React.isValidElement(child) ? child : this.renderGroup(child))
                                }
                            </div>)
                        }
                    </div>
                    {
                        valueHint &&
                        <div className="group-value-hint">{valueHint}</div>
                    }
                </div>
            </div>
        </div>;
    };

    renderContentItems = () => {
        const {layoutConfig, fieldConfigs, fieldsConfig} = this.props;
        return fieldConfigs !== undefined
            ? fieldConfigs.filter(config => !config.hidden).map(config => this.renderField(config))
            : layoutConfig.map(item => {
                    if (React.isValidElement(item)) {
                        return item;
                    }

                    if (typeof item === 'string') {
                        return this.renderField(Object.assign(fieldsConfig[item], {key: item}));
                    }

                    return this.renderGroup(item);
                }
            );
    };

    renderActionBar = () => {
        const {actionsTemplate} = this.props;

        const groups = actionsTemplate.map((groupItems, groupIndex) => {
            const elements = groupItems.map((item, index) => {
                if (React.isValidElement(item)) {
                    return item;
                }

                switch (item) {
                    case 'submitButton':
                        return this.renderSubmitButton();
                    case 'cancelButton':
                        return this.renderCancelButton();
                    default:
                        throw new Error(`Invalid action item type: ${item}`);
                }
            }).filter(Boolean);

            return <div className="group" key={groupIndex}>
                {
                    elements.map((el, index) => <div
                        className="item"
                        key={index}
                    >
                        {el}
                    </div>)
                }
            </div>;
        });

        if (groups.length === 0) {
            return null;
        }

        return <div className="form-actions">
            {groups}
        </div>;
    };

    renderSubmitButton = () => {
        const {
            submitText, submittingText,
            submit, submitWYS, submitChanges,
            disabled,
            buttonSize,
            canSubmitWhenNoChange,
            allowsSubmit,
        } = this.props;

        const {values, errors, isSubmitting} = this.state;

        const hasChanges = this.hasChangesToSubmit();
        const hasErrorAlerts = this.hasErrorAlerts();

        const canSubmit = allowsSubmit(canSubmitWhenNoChange || hasChanges, values, errors, hasErrorAlerts);

        if (!(submit || submitWYS || submitChanges)) {
            return null;
        }

        return <Button
            type="submit"
            title={isSubmitting ? submittingText : submitText}
            appearance={isSubmitting ? 'text-primary' : 'solid-primary'}
            size={buttonSize}
            disabled={disabled || !canSubmit}
        />;
    };

    renderCancelButton = () => {
        const {
            cancel,
            cancelText,
            disabled,
            buttonSize,
            allowsCancel,
        } = this.props;

        const {values, errors, isSubmitting} = this.state;

        const hasChanges = this.hasChangesToSubmit();
        const hasErrorAlerts = this.hasErrorAlerts();

        const canCancel = allowsCancel(hasChanges, values, errors, hasErrorAlerts);

        if (!cancel || !canCancel) {
            return null;
        }

        return <Button
            title={cancelText}
            disabled={disabled || isSubmitting}
            appearance="text-neutral"
            size={buttonSize}
            onClick={this.cancel}
        />;
    };

    hasErrorAlerts = () => {
        const {errors, fieldsTouched, submitError} = this.state;

        if (submitError !== null) {
            return true;
        }

        for (let field in errors) {
            if (errors.hasOwnProperty(field)) {
                if (fieldsTouched[field]) {
                    return true;
                }
            }
        }

        return false;
    }

    render() {
        const {
            contentItemLayout,
            disabled,
            actionButtonsHidden,
            canSubmitWhenNoChange,
            allowsSubmit,
        } = this.props;

        const {values, errors, isSubmitting, submitError} = this.state;

        const hasChanges = this.hasChangesToSubmit();
        const hasErrorAlerts = this.hasErrorAlerts();

        const canSubmit = allowsSubmit(canSubmitWhenNoChange || hasChanges, values, errors, hasErrorAlerts);

        return <form
            className={'custom-form' + (isSubmitting ? ' is-submitting' : '') + (disabled ? ' disabled' : '')}
            onSubmit={ev => {
                ev.preventDefault();
                if (canSubmit) {
                    this.submit();
                }
            }}
            ref={el => this.form = el}
        >
            <div className={`form-content item-layout-${contentItemLayout}`}>
                {this.renderContentItems()}
            </div>
            {
                submitError &&
                <div className="form-error">
                    <Icon name="caution-circle"/>
                    <span className="text">
                        {submitError}
                    </span>
                </div>
            }
            {
                !actionButtonsHidden &&
                this.renderActionBar()
            }
            {
                isSubmitting &&
                <div className="form-prevent-edit-overlay"/>
            }
        </form>;
    }
}

CustomForm.defaultProps = {
    fieldsConfig: {},
    layoutConfig: [],
    defaultValues: {},
    onInitialized: undefined,
    onSetSubmitInvoker: undefined,
    onSetCheckHasChangesInvoker: undefined,
    onSetForceFieldsTouchedInvoker: undefined,
    onChange: undefined,
    submit: undefined,
    submitWYS: undefined,
    submitChanges: undefined,
    onSubmittingChange: undefined,
    submitText: translate('Submit'),
    submittingText: translate('Processing...'),
    disabled: false,
    buttonSize: 'medium',
    allowsSubmit: (hasChanges, values, errors, hasErrorAlerts) => hasChanges,
    cancel: undefined,
    cancelText: translate('Cancel'),
    allowsCancel: (hasChanges, values, errors, hasErrorAlerts) => true,
    contentItemLayout: 'top-down', // top-down, side-by-side
    actionsTemplate: [['submitButton', 'cancelButton']],

    // DEPRECATED
    fieldConfigs: undefined,
    canSubmitWhenNoChange: false,
    actionButtonsHidden: false,
};
